import { SettingsIcon } from '@chakra-ui/icons'
import {
  IconButton,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  Portal,
  Radio,
  RadioGroup,
  Spinner,
  Text,
  VStack,
} from '@chakra-ui/react'
import { useAuth } from '@hb/react'
import { DEFAULT_ENCOUNTER_INPUT_SIZE, InputSize } from '@hb/shared'
import { useState } from 'react'
import { useChartingFormSettings } from '../hooks/useChartingFormSettings'

const INPUT_SIZE_OPTIONS: Array<{ value: InputSize; label: string }> = [
  { value: 'xs', label: 'Extra Small' },
  { value: 'sm', label: 'Small' },
  { value: 'md', label: 'Medium' },
  { value: 'lg', label: 'Large' },
]

// Settings cog in the title row — per-user encounter form preferences, saved to
// charting-form-settings/{uid}. Changes apply live via the doc subscription in
// EncounterFormContent; no save button.
export const EncounterFormSettingsPopover = () => {
  const { authUser } = useAuth()
  const { data: settings, set } = useChartingFormSettings()
  // Size whose write is in flight — the clicked option shows a spinner until the
  // set() promise settles (set() reports failures via toast and never throws).
  const [pendingSize, setPendingSize] = useState<InputSize | null>(null)

  if (!authUser) return null

  const savedSize = settings?.encounterForms?.inputSize ?? DEFAULT_ENCOUNTER_INPUT_SIZE
  // Optimistically select the pending size so the radio moves on click, not on ack.
  const inputSize = pendingSize ?? savedSize
  const handleInputSizeChange = async (value: string) => {
    setPendingSize(value as InputSize)
    try {
      // Merge write, spreading the existing encounterForms map — Firestore merge is
      // per-top-level-map here, so we must carry sibling keys forward ourselves.
      await set(
        { encounterForms: { ...settings?.encounterForms, inputSize: value as InputSize } },
        true,
      )
    } finally {
      setPendingSize(null)
    }
  }

  return (
    <Popover strategy="fixed" trigger="click" placement="bottom-end">
      <PopoverTrigger>
        <IconButton
          borderRadius="full"
          size="sm"
          variant="ghost"
          opacity={0.5}
          aria-label="Encounter form settings"
          icon={<SettingsIcon w={4} h={4} />}
        />
      </PopoverTrigger>
      <Portal>
        <PopoverContent w="280px" _dark={{ bg: 'gray.800', borderColor: 'gray.600' }}>
          <PopoverHeader
            borderTopRadius={4}
            bg="gray.50"
            _dark={{ bg: 'gray.900' }}
            py={1}
            fontWeight={500}
            fontFamily="hero-new">
            Encounter Form Settings
          </PopoverHeader>
          <PopoverBody>
            <Text fontSize="sm" fontWeight={500} mb={1}>
              Input Size
            </Text>
            <RadioGroup value={inputSize} onChange={handleInputSizeChange}>
              <VStack align="stretch" spacing={1}>
                {INPUT_SIZE_OPTIONS.map(({ value, label }) => (
                  <Radio
                    key={value}
                    value={value}
                    size="sm"
                    colorScheme="green"
                    isDisabled={pendingSize !== null}>
                    <Text as="span" fontSize="sm">
                      {label}
                    </Text>
                    {pendingSize === value ? <Spinner size="xs" ml={2} color="green.500" /> : null}
                  </Radio>
                ))}
              </VStack>
            </RadioGroup>
          </PopoverBody>
          <PopoverArrow bg="gray.50" />
        </PopoverContent>
      </Portal>
    </Popover>
  )
}
